'use client';
/**
 * RÔLES & ACCÈS — l'écran unique des droits (refonte du 16/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Il y a trop de rubriques, je ne sais plus gérer en autonomie. » (Ines)
 *
 * Trois questions, trois volets, dans l'ordre où on se les pose :
 *
 *   1. PERSONNES — qui porte quels rôles. Les rôles se cumulent ; la plateforme
 *      PROPOSE (d'après la fonction et les casquettes confirmées en septembre,
 *      gardées en archive), le RQ CONFIRME. Rien n'est coché d'office.
 *   2. RÔLES — ce que chaque rôle ouvre, rubrique par rubrique : rien, voir, ou
 *      voir et modifier. La grille part des défauts du moteur ; une case
 *      changée est marquée, et chaque rôle peut revenir à son défaut.
 *   3. RÈGLES — les cinq règles de contexte, en phrases, et ce que chacun voit
 *      réellement, calculé par le moteur.
 *
 * ⚠️ TOUT PASSE PAR LE BROUILLON DU PARAMÉTRAGE (`draft` / `set`) et par son
 * bouton « Enregistrer » : rôles (`gedUsers[].roles`) et grille (`rolesAcces`)
 * sont écrits par `PUT /ged-setting`, sous la garde du RQ, avec le journal des
 * habilitations. Le moteur décide ensuite avec ces seules données
 * (`backend/src/roles-acces.ts`) : il n'y a plus de réglage par type de compte
 * ni de casquette.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { card, Skeleton, ChampRecherche } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { enTeteCollant } from '@/shared/Tableau';
import { GED_ROLES } from '@/lib/meta';
import type { GedSettings } from '@/lib/types';

type Ligne = { voir: string[]; modifier: string[] };
type Catalogue = {
  sections: { code: string; famille: string; label: string; regle: string }[];
  rolesAcces: {
    roles: { code: string; libelle: string; famille: 'metier' | 'responsabilite' | 'circuit'; actes: string | null; motif: string }[];
    grille: Record<string, Ligne>; grilleDefaut: Record<string, Ligne>;
    regles: { code: string; titre: string; phrase: string; porte: string }[];
    socleVoir: string[]; socleModifier: string[]; plafondRh: string[];
  };
};
type PersonneAcces = {
  orgId: string; nom: string; profil: string | null; sansCompte: boolean; externe: boolean;
  roles: string[]; proposes: string[]; voit: number; modifie: number;
  rubriquesVoir: string[]; rubriquesModifier: string[];
};
type Acces = { personnes: PersonneAcces[] };

const TITRE_FAMILLE: Record<string, string> = { metier: 'Métiers', responsabilite: 'Responsabilités', circuit: 'Circuit documentaire' };
type Volet = 'personnes' | 'grille' | 'regles';

const memeListe = (a: string[], b: string[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

export function RolesAcces({ draft, set, dirty }: {
  draft: GedSettings;
  set: (patch: Partial<GedSettings>) => void;
  /** Le brouillon porte des modifications non enregistrées : les accès affichés décrivent l'état ENREGISTRÉ. */
  dirty: boolean;
}) {
  const [volet, setVolet] = useState<Volet>('personnes');
  const [cat, setCat] = useState<Catalogue | null>(null);
  const [acces, setAcces] = useState<Acces | null>(null);
  const [erreur, setErreur] = useState('');
  const [charge, setCharge] = useState(false);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/sections', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j?.rolesAcces) { setCat(j); setErreur(''); } else setErreur('Le catalogue des rôles n’a pas pu être lu.'); })
      .catch(() => { if (vivant) setErreur('Le catalogue des rôles n’a pas pu être lu.'); });
    return () => { vivant = false; };
  }, []);

  const chargerAcces = useCallback(async () => {
    setCharge(true);
    try {
      const r = await fetch('/api/ged/ged/roles-acces/apercu', { credentials: 'include', cache: 'no-store' });
      const j = await r.json();
      if (!r.ok) { setErreur(j?.error?.message || 'Les accès par personne n’ont pas pu être calculés.'); return; }
      setAcces(j);
    } catch { setErreur('Le service est injoignable.'); } finally { setCharge(false); }
  }, []);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void chargerAcces(); }, [chargerAcces]);

  if (erreur && !cat) return <p role="alert" style={{ fontSize: 13, color: 'var(--amber-600)' }}>{erreur}</p>;
  if (!cat) return <Skeleton rows={6} />;

  const roles = cat.rolesAcces.roles;
  const libelle = (code: string) => roles.find((r) => r.code === code)?.libelle || GED_ROLES[code]?.label || code;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Segment ariaLabel="Volet des rôles et accès" valeur={volet} onChange={setVolet} options={[
        { valeur: 'personnes', libelle: 'Personnes', aide: 'Qui porte quels rôles' },
        { valeur: 'grille', libelle: 'Rôles', aide: 'Ce que chaque rôle ouvre' },
        { valeur: 'regles', libelle: 'Règles & accès', aide: 'Les cinq règles, et ce que chacun voit' },
      ]} />
      {erreur && <p role="alert" style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>}

      {volet === 'personnes' && <Personnes draft={draft} set={set} roles={roles} acces={acces} libelle={libelle} />}
      {volet === 'grille' && <Grille draft={draft} set={set} cat={cat} />}
      {volet === 'regles' && (
        <Regles cat={cat} acces={acces} dirty={dirty} chargement={charge} onRelire={() => void chargerAcces()} libelle={libelle} />
      )}
    </div>
  );
}

/* ══════════════════════════ 1. PERSONNES ══════════════════════════ */

function Personnes({ draft, set, roles, acces, libelle }: {
  draft: GedSettings; set: (p: Partial<GedSettings>) => void;
  roles: Catalogue['rolesAcces']['roles']; acces: Acces | null; libelle: (c: string) => string;
}) {
  const [q, setQ] = useState('');
  const parOrgId = useMemo(() => new Map((acces?.personnes || []).map((p) => [p.orgId, p])), [acces]);
  const familles = ['metier', 'responsabilite', 'circuit'] as const;
  const needle = q.toLowerCase().trim();
  const gens = draft.gedUsers
    .map((u, i) => ({ u, i }))
    .filter(({ u }) => !needle || `${u.nom} ${u.fonction || ''}`.toLowerCase().includes(needle))
    .sort((a, b) => a.u.nom.localeCompare(b.u.nom, 'fr'));

  const poser = (i: number, next: string[]) => set({ gedUsers: draft.gedUsers.map((x, j) => (j === i ? { ...x, roles: next } : x)) });
  const aConfirmer = gens.filter(({ u }) => (parOrgId.get(u.id)?.proposes || []).some((r) => !u.roles.includes(r)));

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Une personne porte autant de rôles qu’elle a de métiers et de responsabilités ; ses accès sont la réunion de ses rôles. Un rôle n’ajoute que des « oui ».
        La plateforme <strong>propose</strong> d’après la fonction et les casquettes confirmées en septembre ; vous <strong>confirmez</strong>, puis « Enregistrer ». Le rôle « lecteur » n’ouvre rien de plus que le socle commun.
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher une personne…" style={{ height: 34, fontSize: 12.5, maxWidth: 320 }} />
        {aConfirmer.length > 1 && (
          <Bouton taille="sm" onClick={() => set({ gedUsers: draft.gedUsers.map((x) => ({ ...x, roles: [...new Set([...x.roles, ...(parOrgId.get(x.id)?.proposes || [])])] })) })}>
            Confirmer les {aConfirmer.length} propositions
          </Bouton>
        )}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {gens.map(({ u, i }) => {
          const a = parOrgId.get(u.id);
          const manquants = (a?.proposes || []).filter((r) => !u.roles.includes(r));
          return (
            <div key={u.id} style={{ ...card, padding: '10px 12px', display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{u.nom}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.fonction}</span>
                <span style={{ marginInlineStart: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
                  {a?.sansCompte ? 'sans compte — rien ne s’applique tant qu’il n’existe pas'
                    : a?.externe ? 'compte externe — aucune rubrique, ce n’est pas réglable'
                      : a ? <>voit <strong>{a.voit}</strong> rubriques, en modifie <strong>{a.modifie}</strong>{a.profil === 'superadmin' ? ' (super admin : tout)' : ''}</> : null}
                </span>
              </div>
              {familles.map((f) => (
                <div key={f} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-faint)', width: 128, flex: 'none' }}>{TITRE_FAMILLE[f]}</span>
                  {roles.filter((r) => r.famille === f).map((r) => {
                    const on = u.roles.includes(r.code);
                    const propose = manquants.includes(r.code);
                    return (
                      <button key={r.code} type="button" aria-pressed={on} title={r.motif}
                        onClick={() => poser(i, on ? u.roles.filter((k) => k !== r.code) : [...u.roles, r.code])}
                        style={{ fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '3px 10px', cursor: 'pointer',
                          border: `1px ${propose ? 'dashed' : 'solid'} ${on ? 'var(--cyan-300)' : propose ? 'var(--amber-400)' : 'var(--border-default)'}`,
                          background: on ? 'var(--cyan-50)' : 'var(--surface-card)', color: on ? 'var(--cyan-700)' : propose ? 'var(--amber-700)' : 'var(--text-muted)' }}>
                        {r.libelle}{propose ? ' · proposé' : ''}
                      </button>
                    );
                  })}
                </div>
              ))}
              {manquants.length > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--amber-700)' }}>Proposition : {manquants.map(libelle).join(' + ')}</span>
                  <Bouton taille="sm" onClick={() => poser(i, [...new Set([...u.roles, ...manquants])])}>Confirmer la proposition</Bouton>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════ 2. LA GRILLE ══════════════════════════ */

function Grille({ draft, set, cat }: { draft: GedSettings; set: (p: Partial<GedSettings>) => void; cat: Catalogue }) {
  const ra = cat.rolesAcces;
  const [famille, setFamille] = useState<'metier' | 'responsabilite' | 'circuit'>('metier');
  const colonnes = ra.roles.filter((r) => r.famille === famille);
  const surcharges = draft.rolesAcces?.grille || {};
  const ligneDe = (role: string): Ligne => surcharges[role] || ra.grille[role] || { voir: [], modifier: [] };
  const etat = (role: string, code: string): 0 | 1 | 2 => {
    const l = ligneDe(role);
    return l.modifier.includes(code) ? 2 : l.voir.includes(code) ? 1 : 0;
  };
  const etatDefaut = (role: string, code: string): 0 | 1 | 2 => {
    const l = ra.grilleDefaut[role] || { voir: [], modifier: [] };
    return l.modifier.includes(code) ? 2 : l.voir.includes(code) ? 1 : 0;
  };
  const ecrire = (role: string, ligne: Ligne) => {
    const d = ra.grilleDefaut[role];
    const grille = { ...surcharges };
    // Revenir au défaut = RETIRER la surcharge, pas la recopier : la grille du moteur peut évoluer.
    if (d && memeListe(d.voir, ligne.voir) && memeListe(d.modifier, ligne.modifier)) delete grille[role];
    else grille[role] = ligne;
    set({ rolesAcces: { ...(draft.rolesAcces || {}), grille } });
  };
  const cycler = (role: string, code: string) => {
    const l = ligneDe(role);
    const e = etat(role, code);
    const voir = new Set(l.voir), modifier = new Set(l.modifier);
    if (e === 0) voir.add(code);
    else if (e === 1) { voir.add(code); modifier.add(code); }
    else { voir.delete(code); modifier.delete(code); }
    ecrire(role, { voir: [...voir], modifier: [...modifier] });
  };
  const familles = [...new Set(cat.sections.map((s) => s.famille))];
  const estPlafond = (code: string) => ra.plafondRh.includes(code);
  const estSocle = (code: string) => ra.socleVoir.includes(code);
  const GLYPHE = ['—', 'Voir', 'Voir + modifier'];
  const cellule: React.CSSProperties = { padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--gray-100)', textAlign: 'center' };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Un clic fait tourner la case : rien → voir → voir et modifier. Une case qui s’écarte du défaut du moteur porte un point ; « Défaut » remet le rôle d’origine. Puis « Enregistrer » au pied de la fenêtre.
        Le <strong>socle</strong> (tableau de bord, documents, planning, organigramme, ses congés) est ouvert à tout compte interne quel que soit son rôle ;
        le <strong>plafond RH</strong> (paie, journal RH, prévisionnel, présences, absences) n’est franchi que par « Gestion RH &amp; paie » — et par le super admin, qui a accès à tout.
      </p>
      <Segment ariaLabel="Famille de rôles" valeur={famille} onChange={setFamille} options={[
        { valeur: 'metier', libelle: 'Métiers' }, { valeur: 'responsabilite', libelle: 'Responsabilités' }, { valeur: 'circuit', libelle: 'Circuit documentaire' },
      ]} />
      <div style={{ overflowX: 'auto' }}>
        <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th scope="col" style={{ ...cellule, textAlign: 'start', borderBottom: '1px solid var(--border-default)', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)' }}>Rubrique</th>
              {colonnes.map((r) => {
                const modifie = !!surcharges[r.code];
                return (
                  <th scope="col" key={r.code} title={r.motif} style={{ ...cellule, borderBottom: '1px solid var(--border-default)', minWidth: 110 }}>
                    <div>{r.libelle}</div>
                    {modifie && <Bouton taille="sm" variante="discret" onClick={() => ecrire(r.code, ra.grilleDefaut[r.code] || { voir: [], modifier: [] })}>Défaut</Bouton>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {familles.map((f) => (
              <React.Fragment key={f}>
                <tr><td colSpan={1 + colonnes.length} style={{ ...cellule, textAlign: 'start', fontWeight: 700, fontSize: 'var(--tableau-ligne-compacte-police)', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{f}</td></tr>
                {cat.sections.filter((s) => s.famille === f).map((s) => (
                  <tr key={s.code}>
                    <td style={{ ...cellule, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)' }}>
                      {s.label}
                      {estSocle(s.code) && <span style={{ marginInlineStart: 6, color: 'var(--cyan-700)' }}>· socle</span>}
                      {estPlafond(s.code) && <span style={{ marginInlineStart: 6, color: 'var(--text-faint)' }}>· plafond RH</span>}
                      {s.regle === 'sien' && <span style={{ marginInlineStart: 6, color: 'var(--text-faint)' }}>· chacun la sienne</span>}
                    </td>
                    {colonnes.map((r) => {
                      const e = etat(r.code, s.code);
                      const ecart = e !== etatDefaut(r.code, s.code);
                      const verrou = estPlafond(s.code) && r.code !== 'rh';
                      return (
                        <td key={r.code} style={cellule}>
                          <button type="button" disabled={verrou} onClick={() => cycler(r.code, s.code)}
                            aria-label={`${s.label} — ${r.libelle} : ${GLYPHE[e]}${ecart ? ' (modifié)' : ''}`}
                            title={verrou ? 'Plafond RH : seul le rôle Gestion RH & paie y entre.' : 'Cliquer pour faire tourner'}
                            style={{ minWidth: 92, height: 24, padding: '0 8px', borderRadius: 'var(--radius-pill)', cursor: verrou ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                              border: `1px solid ${e === 2 ? 'var(--green-600)' : e === 1 ? 'var(--cyan-400)' : 'var(--border-default)'}`,
                              background: e === 2 ? 'var(--green-50)' : e === 1 ? 'var(--cyan-50)' : 'var(--surface-card)',
                              color: e === 2 ? 'var(--green-700)' : e === 1 ? 'var(--cyan-700)' : 'var(--text-faint)', opacity: verrou ? 0.5 : 1 }}>
                            {GLYPHE[e]}{ecart ? ' •' : ''}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════ 3. RÈGLES & ACCÈS ══════════════════════════ */

function Regles({ cat, acces, dirty, chargement, onRelire, libelle }: {
  cat: Catalogue; acces: Acces | null; dirty: boolean; chargement: boolean; onRelire: () => void; libelle: (c: string) => string;
}) {
  const [ouvert, setOuvert] = useState('');
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <section style={{ display: 'grid', gap: 6 }}>
        <h4 style={{ margin: 0, fontSize: 13.5 }}>Les cinq règles de contexte</h4>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Elles s’appliquent par-dessus les rôles, dans des cas précis. Elles sont écrites dans le moteur, pas dans des cases : on les lit ici, on ne les coche pas.</p>
        {cat.rolesAcces.regles.map((r) => (
          <div key={r.code} style={{ ...card, padding: '8px 12px', fontSize: 12.5 }}>
            <strong>{r.code} — {r.titre}.</strong> {r.phrase}
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: 13.5 }}>Ce que chacun voit — calculé par le moteur</h4>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Sur l’état <strong>enregistré</strong>, avec la même garde que la vraie session. {dirty && <span style={{ color: 'var(--amber-700)' }}>Vous avez des modifications non enregistrées : enregistrez, puis relisez.</span>}
        </p>
        <div><Bouton taille="sm" onClick={onRelire} chargement={chargement}>Relire</Bouton></div>
        {acces && (
          <div style={{ display: 'grid', gap: 6 }}>
            {acces.personnes.filter((p) => !p.externe).map((p) => {
              const o = ouvert === p.orgId;
              return (
                <div key={p.orgId} style={{ ...card, padding: '8px 12px', fontSize: 12.5 }}>
                  <button type="button" onClick={() => setOuvert(o ? '' : p.orgId)} aria-expanded={o}
                    style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 10, width: '100%', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <strong>{p.nom}</strong>
                    <span style={{ color: 'var(--text-muted)' }}>{p.profil === 'superadmin' ? 'super admin — tout' : p.roles.length ? p.roles.filter((r) => r !== 'lecteur').map(libelle).join(' + ') || 'socle seul' : 'socle seul'}</span>
                    <span style={{ marginInlineStart: 'auto', color: p.sansCompte ? 'var(--text-faint)' : 'var(--text-body)' }}>
                      {p.sansCompte ? `sans compte — verrait ${p.voit}, modifierait ${p.modifie}` : `voit ${p.voit} · modifie ${p.modifie}`}
                    </span>
                  </button>
                  {o && (
                    <div style={{ marginTop: 6, display: 'grid', gap: 3, color: 'var(--text-body)' }}>
                      <div><span style={{ fontWeight: 600 }}>Voit</span> : {p.rubriquesVoir.join(', ') || '—'}</div>
                      <div><span style={{ fontWeight: 600 }}>Modifie</span> : {p.rubriquesModifier.join(', ') || '—'}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
